/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import { Backdrop, Box, Button, Chip, CircularProgress, Typography, useTheme } from "@mui/material"
import { collection, doc, onSnapshot, DocumentSnapshot, query, where, orderBy, addDoc, deleteDoc, getDocs } from "firebase/firestore"
import { db } from "../../firebase"
import { tokens } from "../../theme"
import { DataGrid, gridNumberComparator } from "@mui/x-data-grid"
import ProcuctForm from "./lib/ProductForm"
import { useParams } from "react-router-dom"
import ShippingDestinationForm from "./lib/ShippingDestinationForm"
import { printProductLabel } from "./lib/PrintLabel"
import env from "../../env"
import dayjs from "dayjs"

interface IPartPrice {
  id: string
  part: string
  priceUnit: 'perPiece' | 'perGram'
  frozenPrice: number
  chilledPrice: number
  description: string
}

const ProductDetails = () => {
  const urlParams = useParams<{ individualId: string }>()
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const [individualInfo, setIndividualInfo] = useState<Record<string,string>>()
  const [items, setItems] = useState<Array<Record<string, any>>>([])
  const [prosessing, setProcessing] = useState<boolean>(false)
  const [open, setOpen] = useState<boolean>(false)
  const [openShipping, setOpenShipping] = useState<boolean>(false)
  const [selectedProductId, setSelectedProductId] = useState<string>('')
  const [partPrices, setPartPrices] = useState<IPartPrice[]>([])
  const productsRef = collection(db, 'products')
  const individualBaseUrl = env('INDIVIDUAL_NO_BASE_URL')

  // 部位別価格マスタの取得
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'partPrices'), (snapshot) => {
      setPartPrices(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as IPartPrice)))
    })
    return unsubscribe
  }, [])

  // 合計重量を計算
  const totalWeight = items.reduce((sum, item) => {
    const weight = parseFloat(item.weight) || 0
    return sum + weight
  }, 0)

  // 製品の金額を計算する関数
  const calculatePrice = (item: Record<string, any>): number => {
    const partPrice = partPrices.find(p => p.part === item.part)
    if (!partPrice) return 0

    const weightInGrams = parseFloat(item.weight) || 0
    const weightInKg = weightInGrams / 1000
    const pricePerUnit = (item.storageType === '冷凍' || !item.storageType) ? partPrice.frozenPrice : partPrice.chilledPrice

    return partPrice.priceUnit === 'perPiece' ? Math.round(pricePerUnit) : Math.round(pricePerUnit * weightInKg)
  }

  // 合計金額を計算
  const totalPrice = items.reduce((sum, item) => sum + calculatePrice(item), 0)

  const columns = [
    {
      field: "productNo",
      maxWidth: 100,
      headerName: "製品番号",
      sortComparator: gridNumberComparator,
    },
    {
      field: "processingDate",
      maxWidth: 80,
      headerName: "加工日",
      flex: 1,
    },
    {
      field: "type",
      maxWidth: 80,
      headerName: "獣種",
      flex: 1,
    },
    {
      field: "part",
      headerName: "部位",
      flex: 1,
    },
    {
      field: "weight",
      maxWidth: 80,
      headerName: "重量(g)",
      flex: 1,
    },
    {
      field: "expirationDate",
      maxWidth: 80,
      headerName: "消費期限",
      flex: 1,
    },
    {
      field: "barcode",
      maxWidth: 100,
      headerName: "バーコード",
      flex: 1,
      renderCell: (params: any) => (
        <Chip
          label="ラベル印刷"
          color="warning"
          size="small"
          onClick={() => printProductLabel({
            name: `${params.row.type}肉`,
            part: params.row.part,
            weight: params.row.weight,
            expirationDate: params.row.expirationDate,
            individualUrl: `${individualBaseUrl}${params.row.individualNo}`,
            productNo: params.row.productNo
          })}
        />
      ),
    },
    {
      field: "destination",
      headerName: "出荷先",
      cellClassName: "name-column--cell",
      flex: 1,
      renderCell: (params: any) => (
        <Chip label={params.row.customer || "ー"} color={params.row.customer ? "info" : "default"} size="small" onClick={() => openShippingDetail(params.row.id)}></Chip>
      ),
    },
    {
      field: "詳細",
      headerName: "詳細",
      flex: 1,
      renderCell: (params: any) => (
        <Box display="flex" justifyContent="center">
          <Box mr={1}><Chip label="編集" color="default" size="small" onClick={() => openDetail(params.row.id)}></Chip></Box>
          <Box mr={1}><Chip label="複製" color="secondary" size="small" onClick={() => copyProduct(params.row.id)}></Chip></Box>
          <Box><Chip label="削除" color="error" size="small" onClick={() => deleteProduct(params.row.id)}></Chip></Box>
        </Box>
      ),
    }
  ]

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(collection(db, 'individuals'), urlParams.individualId), (doc: DocumentSnapshot) => {
      doc.exists() && setIndividualInfo(doc.data())
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    if(!individualInfo) return
    const q = query(productsRef, where('individualNo', '==', individualInfo.individualNo), orderBy("processingDate", "desc"))
    const unsubscribe = onSnapshot(q, ({ docs }) => {
      return setItems(docs.map((_, i) => {
        return ({ No: i + 1, ..._.data(), id: _.id })
      }))
    })
    return unsubscribe
  }, [individualInfo])

  const openDetail = (productId: string) => {
    productId && setSelectedProductId(productId)
    setOpen(true)
  }

  const closeDetail = () => {
    setSelectedProductId("")
    setOpen(false)
  }

  const openShippingDetail = (productId: string) => {
    productId && setSelectedProductId(productId)
    setOpenShipping(true)
  }

  const closeShippingDetail = () => {
    setSelectedProductId("")
    setOpenShipping(false)
  }

  const copyProduct = async (productId: string) => {
    if (!individualInfo) return
    if (!confirm("製品情報を複製しますか？")) return
    const individualNo = individualInfo.individualNo
    const productToCopy = items.find(item => item.id === productId)

    // 製品が見つからない場合は処理を中止
    if (!productToCopy) return

    // ローカルstateではなくFirestoreから最新の製品一覧を取得して採番
    const productsSnap = await getDocs(query(productsRef, where('individualNo', '==', individualNo)))
    const maxNumericPart = productsSnap.docs.reduce((max, d) => {
      const no: string = d.data().productNo || ''
      const numericPart = parseInt(no.substring(individualNo.length))
      return Math.max(max, isNaN(numericPart) ? 0 : numericPart)
    }, 0)

    // 最大値+1を新しい製品番号とする
    const newNumericPart = maxNumericPart + 1
    const productNo = individualNo + ('000' + newNumericPart).slice(-3)

    // idと表示用のNoフィールドを除去してコピー
    const { id, No, ...productDataWithoutId } = productToCopy // eslint-disable-line @typescript-eslint/no-unused-vars
    await addDoc(productsRef, {...productDataWithoutId, productNo, createdAt: dayjs().format('YYYY/MM/DD hh:mm')})
  }

  const deleteProduct = async (productId: string) => {
    if (!individualInfo) return
    if (!confirm("製品情報を削除しますか？")) return
    await deleteDoc(doc(db, 'products', productId))
  }

  return individualInfo && (
    <Box m={5}>
      <Box display="flex" justifyContent="start" alignContent="center">
        <Box display="flex" m={1} justifyContent="center">
          <Button variant="contained" color="info" onClick={() => openDetail('')}>製品情報追加</Button>
        </Box>
      </Box>

      {/* 合計重量・金額表示 */}
      <Box
        m={2}
        p={2}
        sx={{
          backgroundColor: colors.primary[400],
          borderRadius: "8px",
          display: "flex",
          justifyContent: "flex-end",
          gap: 4
        }}
      >
        <Typography variant="h5" sx={{ color: colors.grey[100] }}>
          合計重量: {totalWeight.toLocaleString()}g ({(totalWeight / 1000).toFixed(2)}kg)
        </Typography>
        <Typography variant="h5" sx={{ color: colors.grey[100] }}>
          合計金額: ¥{totalPrice.toLocaleString()}
        </Typography>
      </Box>
      <Box
        m={2}
        height="80vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        <DataGrid
          rows={items}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [{ field: 'productNo', sort: 'desc'}],
            }
          }}
        />
      </Box>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={prosessing}
        onClick={() => setProcessing(false)}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <ShippingDestinationForm
        productId={selectedProductId}
        isOpen={openShipping}
        onClose={closeShippingDetail}
      />
      <ProcuctForm
        individualNo={individualInfo.individualNo}
        productId={selectedProductId}
        isOpen={open}
        onClose={closeDetail}
      />
    </Box>
  )
}

export default ProductDetails
